// Rules model + builder-time checks for a skip-logic section.
//
// A section's payload is the rules JSON the server reads and evaluates. Shape
// (version 1):
//   { version: 1,
//     rules: [ { id, when: {all|any: [...]}, goTo: <dest> } ],
//     otherwise: <dest> }
// where <dest> is { next: true } | { end: true } | { item: <id> }. Rules are
// checked top to bottom and the FIRST that matches decides where the respondent
// goes, so array order is the only priority signal.
//
// The condition ("if") side is authored in the condition editor; here a new rule
// starts with an empty `all` group and a forward-only destination. The server's
// parser is strict - no property outside this shape may be sent - so UI-only
// state is kept outside the rules object, not on conditions or rules.
//
// Validation here is a lightweight, pre-save convenience so the picker can flag
// an obviously backward/missing destination. The authoritative check is the
// server's route validation; the builder renders those problems when it saves.

import {validateDestination} from './destinations'

export const RULE_SCHEMA_VERSION = 1

// A rule id unique within a section. Ids live in the rules JSON and the engine's
// trace, and must match [A-Za-z0-9_-]{1,64}.
const newRuleId = () => `r${Math.random().toString(36).slice(2, 10)}`

// Destination factories.
export const nextDestination = () => ({next: true})
export const endDestination = () => ({end: true})
export const itemDestination = item => ({item})

// A fresh rules object: no rules yet, fall through to the following item.
export const emptyRules = () => ({
  version: RULE_SCHEMA_VERSION,
  rules: [],
  otherwise: {next: true},
})

// A fresh rule: an empty "all" group (an empty group is refused on save, so a
// condition is added before the rule is sent) and, as a safe default, a route
// to the end of the survey, which is always a valid forward destination.
export const emptyRule = () => ({
  id: newRuleId(),
  when: {all: []},
  goTo: {end: true},
})

// Group helpers: a rule's `when` is a single-key object, {all:[...]} or
// {any:[...]}. These read and switch the mode while keeping the conditions.
export const groupMode = when => (Array.isArray(when?.any) ? 'any' : 'all')
export const groupConditions = when => when?.[groupMode(when)] || []
export const setGroupMode = (when, mode) => ({[mode]: groupConditions(when)})

// A stable string key for a destination, used as a <select> option value.
export const goToKey = dest => {
  if (!dest) return ''
  if (dest.item != null) return `item:${dest.item}`
  if (dest.end === true) return 'end'
  if (dest.next === true) return 'next'
  return ''
}

const DESTINATION_MESSAGES = {
  'destination-not-forward':
    'Choose a later item or the end of the survey - rules can only move forwards.',
  'destination-is-skip-logic':
    'Choose a question or content screen, not another skip-logic section.',
  'destination-missing': 'Choose where this rule sends people.',
}

// Save-time convenience check: every rule's destination and the otherwise route
// must be a valid forward-only destination. Returns [{code, message}] keyed so
// the editor can show each message against the right control.
export const validateRules = (survey, sectionId, rules, sectionIndex) => {
  const errors = []
  ;(rules?.rules || []).forEach(rule => {
    const result = validateDestination(survey, sectionId, rule.goTo, sectionIndex)
    if (!result.valid) {
      errors.push({
        code: `rule-goto:${rule.id}`,
        message: DESTINATION_MESSAGES[result.code],
      })
    }
  })
  const otherwise = validateDestination(survey, sectionId, rules?.otherwise, sectionIndex)
  if (!otherwise.valid) {
    errors.push({code: 'otherwise-goto', message: DESTINATION_MESSAGES[otherwise.code]})
  }
  return errors
}
